import { reactive } from 'vue'
import {
  applyProgressEvent,
  createProgressState,
  endStep,
  finishProgress,
  readPromoterResponse,
  startStep,
} from './promoterProgress.ts'
import type { ProgressState } from './promoterProgress.ts'

const DONE_LINGER_MS = 8000
const FAILED_LINGER_MS = 30000

export type PromoterTaskEntry = { id: string, state: ProgressState }

// Every running or recently finished promoter task, oldest first.
// PromoterProgressPanel renders one card per entry, so concurrent tasks
// never overlap the way stacked toasts do.
export const promoterTasks = reactive<PromoterTaskEntry[]>([])

const removalTimers = new Map<string, ReturnType<typeof setTimeout>>()
let nextId = 0

export function dismissPromoterTask(id: string) {
  clearTimeout(removalTimers.get(id))
  removalTimers.delete(id)
  const index = promoterTasks.findIndex((task) => task.id === id)
  if (index >= 0) promoterTasks.splice(index, 1)
}

export function startPromoterTask(title: string) {
  const id = `promoter-${++nextId}`
  promoterTasks.push({ id, state: createProgressState(title) })
  // Mutate through the reactive proxy so the panel re-renders.
  const state = promoterTasks[promoterTasks.length - 1]!.state
  const finish = (ok: boolean, summary: string) => {
    finishProgress(state, ok, summary)
    clearTimeout(removalTimers.get(id))
    removalTimers.set(id, setTimeout(() => dismissPromoterTask(id), ok ? DONE_LINGER_MS : FAILED_LINGER_MS))
  }

  return {
    // POSTs with a progress stream; a non-2xx result throws an error shaped
    // like $fetch's (message + data), so callers keep their error handling.
    async request<T = any>(url: string, headers: Record<string, string>, body: unknown): Promise<T> {
      const response = await fetch(url, {
        method: 'POST',
        headers: { ...headers, 'accept': 'application/x-ndjson', 'content-type': 'application/json' },
        body: JSON.stringify(body),
      })
      const result = await readPromoterResponse(response, (event) => applyProgressEvent(state, event))
      if (result.status >= 400) {
        const message = result.body?.error || result.body?.message || `HTTP ${result.status}`
        throw Object.assign(new Error(message), { data: result.body, statusCode: result.status })
      }
      return result.body as T
    },
    step: (stepId: string, label: string) => startStep(state, stepId, label),
    endStep: (stepId: string, ok: boolean, label?: string) => endStep(state, stepId, ok, Date.now(), label),
    succeed: (summary: string) => finish(true, summary),
    fail: (summary: string) => finish(false, summary),
  }
}
